import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { differenceInMonths, format } from 'date-fns';
import { LoaderCircle } from 'lucide-preact';
import { useState } from 'preact/hooks';

import { Button } from '@/components/Buttons';
import Loader from '@/components/Loader';
import CancelReasonsModal from '@/components/products/CancellationOffer';
import { ProductType } from '@/components/products/ProductsPromptModal/redux/types';
import { handlePatchUserProductSuccess } from '@/components/products/utils';
import { useLatestActiveTime } from '@/hooks/useLatestActiveDateTime';
import { getProductsQueryOptions, patchUserProductMutationFn } from '@/queries/products';
import { GetProductsResponse, ParsedUserProduct, Product } from '@/queries/products/types';

import ProductTag from './ProductTag';
import PromotedProductSection from './PromotedProductSection';
import UserProductImage from './UserProductImage';
import { getProductDescription, getProductMonthlyPrice, getProductPrice, getProductTitle } from './utils';

interface RecurringProductDetailsProps {
  userProduct: ParsedUserProduct;
}

const isMonthlyExplorerPass = (productLookupKey: string | undefined) => {
  return productLookupKey === 'monthly-member';
};

const getLookupKey = (productLookupKey: string | undefined) => {
  switch (productLookupKey) {
    case 'monthly-member':
    case 'annual-member':
      return 'monthly-member,annual-member';
    case 'monthly-educator':
    case 'annual-educator':
      return 'monthly-educator,annual-educator';
    default:
      return undefined;
  }
};

const getSwitchableProduct = (productLookupKey: string | undefined, productsMap: Map<string, Product> | undefined) => {
  if (!productsMap) return undefined;
  switch (productLookupKey) {
    case 'monthly-member':
      return productsMap.get('annual-member');
    case 'annual-member':
      return productsMap.get('monthly-member');
    case 'monthly-educator':
      return productsMap.get('annual-educator');
    case 'annual-educator':
      return productsMap.get('monthly-educator');
    default:
      return undefined;
  }
};

const getPromotedProductType = (productLookupKey: string | undefined): ProductType | undefined => {
  switch (productLookupKey) {
    case 'monthly-member':
    case 'annual-member':
      return 'educator-pass';
    case 'monthly-educator':
    case 'annual-educator':
      return 'explorer-pass';
    default:
      return undefined;
  }
};

const selectProductsMap = (data: GetProductsResponse) => {
  return data.list.reduce((acc, product) => {
    if (!product.productLookupKey) return acc;
    acc.set(product.productLookupKey, product);
    return acc;
  }, new Map<string, Product>());
};

const RecurringProductDetails = ({ userProduct }: RecurringProductDetailsProps) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [processingButtonId, setProcessingButtonId] = useState<string | null>(null);
  const lookupKey = getLookupKey(userProduct.product.productLookupKey);
  const [now] = useLatestActiveTime();

  const queryClient = useQueryClient();
  const { mutate: patchUserProduct, isPending } = useMutation({
    mutationFn: patchUserProductMutationFn,
    onSuccess: handlePatchUserProductSuccess(queryClient),
    onSettled: () => {
      setProcessingButtonId(null);
    },
  });

  const { data: productsMap, isLoading } = useQuery({
    ...getProductsQueryOptions({
      lookupKey,
    }),
    enabled: !!lookupKey,
    select: selectProductsMap,
  });

  const currentProduct = productsMap?.get(userProduct.product.productLookupKey ?? '');

  if (isLoading) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <Loader width="64px" height="64px" />
      </div>
    );
  }

  if (!currentProduct) {
    return <div className="flex min-h-64 items-center justify-center">Unknown product</div>;
  }

  const switchableProduct = getSwitchableProduct(userProduct.product.productLookupKey, productsMap);
  const subscribedMonths = differenceInMonths(now, userProduct.createdAt);
  const canCancel = !isMonthlyExplorerPass(userProduct.product.productLookupKey) || subscribedMonths >= 3;

  const handleResume = () => {
    patchUserProduct({
      userProductId: userProduct._id,
      action: 'resume',
    });
  };

  const handleTierChange = (product: Product) => {
    patchUserProduct({
      userProductId: userProduct._id,
      action: 'tierChange',
      targetProductId: product._id,
      targetPriceId: product.price.priceId,
    });
  };

  const handleCancel = (cancellationReason?: string) => {
    setProcessingButtonId('current-product-button');
    patchUserProduct({
      userProductId: userProduct._id,
      action: 'cancel',
      cancellationReason: cancellationReason ?? 'User cancelled',
    });
    setShowCancelModal(false);
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const promotedProductType = getPromotedProductType(userProduct.product.productLookupKey);
  const currentProductPrice = getProductPrice(userProduct.product.price);

  const isSwitchingProduct = userProduct.scheduledProductId !== userProduct.productId;
  const isSwitchingToSwitchableProduct = userProduct.scheduledProductId === switchableProduct?._id;

  return (
    <div>
      <div
        className={clsx(
          'preflight divide-y divide-stone-200 desktop:px-6',
          promotedProductType && 'mobile:border-b mobile:border-stone-200 desktop:shadow-[2px_2px_7px_0px_rgba(0,0,0,0.25)]',
        )}
      >
        {/* current product details */}
        <div className="preflight flex items-center gap-3 py-5 mobile:flex-col mobile:px-3">
          <div className="flex min-w-0 items-center gap-3 mobile:w-full desktop:grow">
            <UserProductImage className="size-11 shrink-0 desktop:size-[60px]" />
            <div className="min-w-0 mobile:grow">
              <h6 className="preflight preflight-h6 line-clamp-1 text-lg font-bold text-neutral-600 desktop:text-2xl">
                {getProductTitle(currentProduct.productLookupKey)}
              </h6>
              <div className="flex justify-between gap-2 text-base text-stone-500">
                <p className="preflight preflight-p line-clamp-1 desktop:text-xl">
                  {getProductDescription(userProduct, now)}
                </p>
                <div className="desktop:hidden">{currentProductPrice}</div>
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-6 mobile:w-full">
            <div className="text-lg text-stone-500 mobile:hidden">{currentProductPrice}</div>
            <Button
              data-id="current-product-button"
              variant={userProduct.cancellationInfo ? 'default' : 'outline'}
              disabled={isPending || !canCancel}
              type="button"
              className="min-w-40 shrink-0 px-5 py-3 text-base/none font-bold mobile:w-full"
              onClick={() => {
                setProcessingButtonId('current-product-button');
                if (isSwitchingProduct) {
                  handleResume();
                } else {
                  handleCancelClick();
                }
              }}
            >
              {!canCancel ? (
                `${3 - subscribedMonths} month${3 - subscribedMonths !== 1 ? 's' : ''} left before cancel`
              ) : isPending && processingButtonId === 'current-product-button' ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : isSwitchingProduct ? (
                'Resume'
              ) : (
                'Cancel'
              )}
            </Button>
          </div>
        </div>
        {/* switchable product details */}
        {switchableProduct && (
          <div className="preflight py-5 mobile:px-3">
            <div className="flex items-center gap-3 mobile:flex-col">
              <div className="flex min-w-0 items-center gap-3 mobile:w-full desktop:grow">
                <UserProductImage className="size-11 shrink-0 desktop:size-[60px]" />
                <div>
                  <h6 className="preflight preflight-h6 line-clamp-1 text-lg font-bold text-neutral-600 desktop:text-2xl">
                    {getProductTitle(switchableProduct.productLookupKey)}
                  </h6>
                  <p className="preflight preflight-p line-clamp-1 text-base text-stone-500 desktop:text-xl">
                    {getProductMonthlyPrice(switchableProduct.price)}
                  </p>
                </div>
              </div>
              {switchableProduct.price.interval === 'year' && (
                <div className="flex shrink-0 items-center gap-2 mobile:w-full desktop:mr-3">
                  {switchableProduct.price.metadata?.tag && <ProductTag>{switchableProduct.price.metadata.tag}</ProductTag>}
                  <div className="text-base text-stone-500 desktop:text-lg">{getProductPrice(switchableProduct.price)}</div>
                </div>
              )}
              <div className="mobile:w-full">
                <Button
                  data-id="switchable-product-button"
                  variant={isSwitchingToSwitchableProduct ? 'outline' : 'default'}
                  disabled={isPending}
                  type="button"
                  className="min-w-40 shrink-0 px-5 py-3 text-base/none font-bold mobile:w-full"
                  onClick={() => {
                    setProcessingButtonId('switchable-product-button');
                    if (isSwitchingToSwitchableProduct) {
                      handleResume();
                    } else {
                      handleTierChange(switchableProduct);
                    }
                  }}
                >
                  {isPending && processingButtonId === 'switchable-product-button' ? (
                    <LoaderCircle className="size-4 animate-spin" />
                  ) : isSwitchingToSwitchableProduct ? (
                    'Cancel Switch'
                  ) : (
                    `Switch to ${switchableProduct.price.interval === 'year' ? 'Yearly' : 'Monthly'} Billing`
                  )}
                </Button>
              </div>
            </div>
            <p className="preflight preflight-p mt-4 text-base text-stone-500 desktop:text-lg">
              By switching, you will pay on {format(userProduct.validUntil, 'MMMM d yyyy')}, after your current plan expires
            </p>
          </div>
        )}
      </div>
      {/* promoted product details */}
      {promotedProductType && (
        <PromotedProductSection
          productType={promotedProductType}
          userProduct={userProduct}
          onSwitch={handleTierChange}
          isPending={isPending}
          processingButtonId={processingButtonId}
          setProcessingButtonId={setProcessingButtonId}
          onResume={handleResume}
        />
      )}
      {showCancelModal && (
        <CancelReasonsModal
          userProduct={userProduct}
          productLookupKey={userProduct.product.productLookupKey}
          onClose={() => setShowCancelModal(false)}
          onConfirmCancel={handleCancel}
        />
      )}
    </div>
  );
};

export default RecurringProductDetails;
